import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Card, DatePicker, Input, List, Modal, Radio, Spin, Tag,
  Table, Tooltip, Typography, message,
} from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import dayjs from 'dayjs';
import {
  fetchDocumo, downloadDocumo,
  searchPatientsForFax, saveFaxMetadata, manualMapFax, unassignFax, findPatientUserRefByUli,
} from '../../redux/doctorSlice';
import { DownloadOutlined, EyeOutlined, UserAddOutlined, UserDeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Faxes go back years, so default to a recent window instead of loading everything.
const DEFAULT_MONTHS_BACK = 3;
const defaultRange = () => [dayjs().subtract(DEFAULT_MONTHS_BACK, 'month'), dayjs()];

// Built on each render so a long-lived tab never offers a stale "last N" window.
const rangePresets = () => [
  { label: 'Last 30 days', value: [dayjs().subtract(30, 'day'), dayjs()] },
  { label: 'Last 3 months', value: [dayjs().subtract(3, 'month'), dayjs()] },
  { label: 'Last 6 months', value: [dayjs().subtract(6, 'month'), dayjs()] },
  { label: 'Last 12 months', value: [dayjs().subtract(12, 'month'), dayjs()] },
];

const Fax = () => {
  const dispatch = useDispatch();
  const documoData = useSelector((state) => state.doctor.documoData);
  const documoLoading = useSelector((state) => state.doctor.documoLoading);
  const faxSearchResults = useSelector((s) => s.doctor.faxPatientSearchResults);
  const faxSearchLoading = useSelector((s) => s.doctor.faxSearchLoading);
  const savingMeta = useSelector((s) => s.doctor.savingFaxMetadata);
  const mappingFax = useSelector((s) => s.doctor.mappingFax);
  const unassigningFax = useSelector((s) => s.doctor.unassigningFax);
  const isAdmin = useSelector((s) => s.authentication?.userAuth?.obj?.role === 'SuperAdmin');

  const blobUrlCacheRef = useRef(new Map());

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMessageNumber, setPreviewMessageNumber] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });

  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState(defaultRange);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignRecord, setAssignRecord] = useState(null);
  const [metaFirst, setMetaFirst] = useState('');
  const [metaLast, setMetaLast] = useState('');
  const [metaUli, setMetaUli] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [assignPdfUrl, setAssignPdfUrl] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [unassigningMessageNumber, setUnassigningMessageNumber] = useState(null);

  useEffect(() => {
    const [start, end] = dateRange;
    dispatch(fetchDocumo({
      startdate: start.format('YYYY-MM-DD'),
      enddate: end.format('YYYY-MM-DD'),
    }));
  }, [dispatch, dateRange]);

  const handleTableChange = (newPagination) => setPagination(newPagination);

  const loadBlobUrl = async (messageNumber) => {
    const cached = blobUrlCacheRef.current.get(messageNumber);
    if (cached) return cached;
    const response = await dispatch(downloadDocumo(messageNumber)).unwrap();
    const blob = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    blobUrlCacheRef.current.set(messageNumber, url);
    return url;
  };

  const handleDownload = async (messageNumber) => {
    try {
      const response = await dispatch(downloadDocumo(messageNumber)).unwrap();
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documo_${messageNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Document downloaded successfully');
    } catch {
      message.error('Failed to download document');
    }
  };

  const handleView = async (messageNumber) => {
    try {
      setPreviewMessageNumber(messageNumber);
      const url = await loadBlobUrl(messageNumber);
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch {
      message.error('Failed to load document preview');
    }
  };

  const onClosePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl(null);
    setPreviewMessageNumber(null);
  };

  const handleOpenAssign = async (record) => {
    const parts = (record.patientName || '').split(' ');
    setAssignRecord(record);
    setMetaFirst(parts[0] || '');
    setMetaLast(parts.slice(1).join(' ') || '');
    setMetaUli(record.pendingPatientULI || '');
    setSelectedPatient(null);
    setHasSearched(false);
    setAssignPdfUrl(null);
    setAssignModalOpen(true);
    try {
      const url = await loadBlobUrl(record.messageNumber);
      setAssignPdfUrl(url);
    } catch {
      // PDF unavailable - modal still functional
    }
  };

  const handleSaveMeta = async () => {
    try {
      await dispatch(saveFaxMetadata({
        messageNumber: assignRecord?.messageNumber,
        firstName: metaFirst,
        lastName: metaLast,
        uli: metaUli,
      })).unwrap();
      message.success('Patient details saved');
    } catch {
      message.error('Failed to save patient details');
    }
  };

  const handleSearch = () => {
    setHasSearched(true);
    setSelectedPatient(null);
    dispatch(searchPatientsForFax({ firstName: metaFirst, lastName: metaLast, uli: metaUli }));
  };

  const handleConfirmMap = async () => {
    try {
      await dispatch(manualMapFax({
        messageNumber: assignRecord.messageNumber,
        userRef: selectedPatient.userRef,
        firstName: metaFirst,
        lastName: metaLast,
        uli: metaUli,
      })).unwrap();
      message.success('Fax successfully mapped to patient');
      setAssignModalOpen(false);
    } catch {
      message.error('Failed to map fax to patient');
    }
  };

  const handleUnassign = async (record) => {
    if (!record.patientRef) {
      message.error('Missing patient reference for this fax; cannot unassign.');
      return;
    }
    setUnassigningMessageNumber(record.messageNumber);
    try {
      // The table only has the patient's ULI (shown as "patientRef") - the
      // unassign endpoint needs their actual UserRef, so resolve it first.
      const userRef = await dispatch(
        findPatientUserRefByUli({ uli: record.patientRef, patientName: record.patientName }),
      ).unwrap();
      await dispatch(unassignFax({ messageNumber: record.messageNumber, userRef })).unwrap();
      message.success('Fax unassigned from patient');
    } catch (err) {
      message.error(err?.message || 'Failed to unassign fax');
    } finally {
      setUnassigningMessageNumber(null);
    }
  };

  useEffect(() => {
    const cache = blobUrlCacheRef.current;
    return () => {
      cache.forEach((url) => { try { window.URL.revokeObjectURL(url); } catch {} });
      cache.clear();
    };
  }, []);

  const previewTitle = useMemo(() => {
    if (!previewMessageNumber) return 'Document preview';
    return `Document preview • ${previewMessageNumber}`;
  }, [previewMessageNumber]);

  const tableData = Array.isArray(documoData) ? documoData : [];

  const filteredData = tableData.filter((r) => {
    if (statusFilter === 'mapped') return r.mappedToPatient;
    if (statusFilter === 'unmapped') return !r.mappedToPatient;
    return true;
  });

  const columns = [
    {
      title: 'Message Number',
      dataIndex: 'messageNumber',
      key: 'messageNumber',
      ellipsis: true,
    },
    {
      title: 'Pages',
      dataIndex: 'pageCount',
      key: 'pageCount',
      width: 100,
    },
    {
      title: 'Patient Name',
      dataIndex: 'patientName',
      key: 'patientName',
    },
    {
      title: 'Patient ULI Number',
      dataIndex: 'patientRef',
      key: 'patientRef',
      render: (patientRef, record) => patientRef || record.pendingPatientULI || '',
    },
    {
      title: 'Status',
      dataIndex: 'mappedToPatient',
      key: 'status',
      width: 120,
      render: (mapped) => (
        <Tag color={mapped ? 'green' : 'orange'}>
          {mapped ? 'Mapped' : 'Unmapped'}
        </Tag>
      ),
    },
    {
      title: 'Received At',
      dataIndex: 'faxReceivedAt',
      key: 'faxReceivedAt',
      render: (date) => moment(date).format('MMMM DD, YYYY HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="View">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleView(record.messageNumber)}
            />
          </Tooltip>
          <Tooltip title="Download">
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record.messageNumber)}
            />
          </Tooltip>
          {!record.mappedToPatient && (
            <Tooltip title="Assign to patient">
              <Button
                type="link"
                icon={<UserAddOutlined />}
                onClick={() => handleOpenAssign(record)}
              />
            </Tooltip>
          )}
          {isAdmin && record.mappedToPatient && (
            <Tooltip title="Unassign from patient">
              <Button
                type="link"
                danger
                icon={<UserDeleteOutlined />}
                loading={unassigningFax && unassigningMessageNumber === record.messageNumber}
                onClick={() => handleUnassign(record)}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ marginTop: 40 }}>
      <Card style={{ border: '1px solid #C2E6F8' }}>
        <div style={{ marginBottom: 20 }}>
          <Text strong>Document History</Text>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <Radio.Group
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <Radio.Button value="all">All</Radio.Button>
            <Radio.Button value="unmapped">Unmapped</Radio.Button>
            <Radio.Button value="mapped">Mapped</Radio.Button>
          </Radio.Group>
          <RangePicker
            value={dateRange}
            onChange={(range) => range?.[0] && range?.[1] && setDateRange(range)}
            presets={rangePresets()}
            allowClear={false}
            format="YYYY-MM-DD"
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
        </div>
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={documoLoading}
          rowKey="messageNumber"
          onChange={handleTableChange}
          pagination={{
            position: ['bottomRight'],
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredData.length,
            showSizeChanger: true,
          }}
          scroll={{ x: true }}
        />
      </Card>

      {/* Standalone fax preview modal */}
      <Modal
        title={previewTitle}
        open={previewOpen}
        onCancel={onClosePreview}
        footer={null}
        width={900}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
      >
        {previewUrl ? (
          <iframe
            title="Fax document preview"
            src={previewUrl}
            style={{ width: '100%', height: '75vh', border: 0 }}
          />
        ) : (
          <div style={{ padding: 24 }}>
            <Text type="secondary">Loading preview…</Text>
          </div>
        )}
      </Modal>

      {/* Assign to patient modal */}
      <Modal
        title={`Assign fax to patient${assignRecord ? ` • ${assignRecord.messageNumber}` : ''}`}
        open={assignModalOpen}
        onCancel={() => setAssignModalOpen(false)}
        footer={null}
        width={1100}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
      >
        <div style={{ display: 'flex', height: '75vh' }}>
          {/* PDF viewer - 60% */}
          <div style={{ flex: '0 0 60%', borderRight: '1px solid #f0f0f0' }}>
            {assignPdfUrl ? (
              <iframe
                title="Fax document"
                src={assignPdfUrl}
                style={{ width: '100%', height: '100%', border: 0 }}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Spin tip="Loading fax…" />
              </div>
            )}
          </div>

          {/* Right panel - 40% */}
          <div style={{ flex: '0 0 40%', padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <Text strong>Patient Details</Text>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Input
                  placeholder="First name"
                  value={metaFirst}
                  onChange={(e) => setMetaFirst(e.target.value)}
                />
                <Input
                  placeholder="Last name"
                  value={metaLast}
                  onChange={(e) => setMetaLast(e.target.value)}
                />
                <Input
                  placeholder="ULI"
                  value={metaUli}
                  onChange={(e) => setMetaUli(e.target.value)}
                />
                <Button
                  onClick={handleSaveMeta}
                  loading={savingMeta}
                  disabled={!metaFirst && !metaLast && !metaUli}
                >
                  Save Details
                </Button>
              </div>
            </div>

            <Button type="primary" onClick={handleSearch} loading={faxSearchLoading} block>
              Search Patients
            </Button>

            {hasSearched && !faxSearchLoading && (
              faxSearchResults.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  No matching patients found. The details you entered have been saved.
                  If a matching patient is created later, the fax will be available to link at that time.
                </Text>
              ) : (
                <List
                  size="small"
                  bordered
                  dataSource={faxSearchResults}
                  style={{ flex: 1, overflowY: 'auto' }}
                  renderItem={(patient) => (
                    <List.Item
                      onClick={() => setSelectedPatient(patient)}
                      style={{
                        cursor: 'pointer',
                        background: selectedPatient?.userRef === patient.userRef ? '#e6f4ff' : undefined,
                      }}
                    >
                      <Text>{patient.firstName} {patient.lastName}</Text>
                      {patient.uli && (
                        <Text type="secondary" style={{ marginLeft: 8 }}>· {patient.uli}</Text>
                      )}
                    </List.Item>
                  )}
                />
              )
            )}

            <Button
              type="primary"
              disabled={!selectedPatient}
              loading={mappingFax}
              onClick={handleConfirmMap}
              style={{ marginTop: 'auto' }}
            >
              Confirm Mapping
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Fax;
